import type { CSSProperties } from 'react';

/**
 * Полный экран у ролика на телефоне — без поворота телефона (задача 6, 11.09.2026).
 *
 * ⚠️ ЖАЛОБА. Развернуть ролик можно было только поворотом, а при заблокированном
 * повороте — никак. Причины на двух системах разные, лечение тоже:
 *
 * - **Android.** Системный полный экран открывался в вертикали, ролик — полоской.
 *   Просим экран лечь горизонтально (`screen.orientation.lock`). Браузер разрешает
 *   это только в полноэкранном режиме — поэтому вызов стоит ПОСЛЕ входа в него.
 * - **iPhone.** Системного разворота для рамки YouTube в Safari нет вовсе: работает
 *   наш слой на весь экран. `lock` там не поддерживается, поэтому в вертикали слой
 *   сам поворачивается на 90° — ролик во весь экран без поворота телефона.
 *
 * ⚠️ Разрешения на полный экран у рамки YouTube по-прежнему НЕТ, и это намеренно:
 * иначе в полный экран ушёл бы кадр YouTube без водяного знака (см. YouTubePlayer).
 */

type OrientationLike = { lock?: (orientation: string) => Promise<void>; unlock?: () => void };
/** `window.screen` глазами этого модуля: в типах DOM у `orientation` нет `lock`. */
export type ScreenLike = { orientation?: OrientationLike };

/** Android — по браузеру или по приложению (`Capacitor.getPlatform()`). */
export function isAndroidDevice(ua: string, platform?: string): boolean {
  return platform === 'android' || /Android/i.test(ua);
}

/**
 * Android WebView: у него в UA стоит `; wv)`. Это основное окно приложения (Capacitor): страница наша, а браузер —
 * не Chrome. Окно ролика с 1.1.2 — Custom Tabs (`@capacitor/browser`), там настоящий Chrome без `wv`.
 */
export function isAndroidWebView(ua: string): boolean {
  return /Android/i.test(ua) && /;\s*wv\)/.test(ua);
}

/**
 * Чем разворачивать ролик (30.09.2026): системным полным экраном или сразу нашим слоем.
 *
 * ⚠️ В ANDROID WEBVIEW — ТОЛЬКО СЛОЙ. Системный полный экран WebView отдаёт приложению
 * (`WebChromeClient.onShowCustomView`), а Capacitor 8.5 там сразу его закрывает
 * (`BridgeWebChromeClient`: первым делом `callback.onCustomViewHidden()`). Обещание
 * `requestFullscreen()` при этом может и не отказать — и запасной слой не включится:
 * ученик жмёт кнопку и не получает ничего. Слой же работает и при запрете поворота
 * приложения (портрет, `AndroidManifest.xml`): в вертикали он сам повёрнут на 90°.
 *
 * ⚠️ В ПРИЛОЖЕНИИ НА iOS — ТОЖЕ ТОЛЬКО СЛОЙ (01.10.2026, сверка CHECK-2026-10-01-VIDEO-FULLSCREEN). В Safari на
 * iPhone полного экрана у контейнера нет, а в WKWebView приложения он ЕСТЬ: Capacitor 8.5 включает
 * `isElementFullscreenEnabled`. `requestFullscreen()` там не откажет, и получится настоящий полный экран WebKit
 * поверх корня «только портрет» — путь, который на устройстве не проверен ни разу; кадр в нём не повернётся при
 * замке поворота. Слой от поворота не зависит вовсе: в вертикали он сам повёрнут на 90°.
 *
 * Chrome на Android и компьютер — системный (+ `lockLandscape` на Android). Safari на iPhone — тоже
 * «системный»: Safari откажет, и сработает запасной слой, как и прежде.
 */
export function fullscreenMode(ua: string): 'system' | 'layer' {
  return isAndroidWebView(ua) || isIosAppWebView(ua) ? 'layer' : 'system';
}

/**
 * Основной WebView приложения на iOS: Capacitor дописывает в UA хвост `1BilimAcademy/<версия> (<сборка>; ios)`
 * (`appendUserAgent` в capacitor.config.ts). У Safari и у окна ролика такого хвоста нет.
 */
export function isIosAppWebView(ua: string): boolean {
  return /1BilimAcademy\/\S+ \(\d+; ios\)/.test(ua);
}

/**
 * Попросить горизонталь. Только Android. Отказ браузера — не ошибка: ролик
 * остаётся в полном экране, просто без поворота.
 */
export async function lockLandscape({
  ua,
  screen,
  platform,
}: {
  ua: string;
  screen: ScreenLike;
  platform?: string;
}): Promise<boolean> {
  if (!isAndroidDevice(ua, platform)) return false;
  const orientation = screen.orientation;
  if (!orientation || typeof orientation.lock !== 'function') return false;
  try {
    await orientation.lock('landscape');
    return true;
  } catch {
    return false;
  }
}

/** Отпустить поворот на выходе из полного экрана. */
export function unlockOrientation(screen: ScreenLike): void {
  try {
    screen.orientation?.unlock?.();
  } catch {
    /* браузер без поворота — отпускать нечего */
  }
}

/**
 * Стиль нашего полноэкранного слоя при вертикальном экране: слой шириной в высоту
 * экрана, повёрнутый на 90°. Горизонтально — `undefined`, слой как есть.
 */
export function rotatedLayerStyle(width: number, height: number): CSSProperties | undefined {
  if (height <= width) return undefined;
  return {
    width: `${height}px`,
    height: `${width}px`,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(90deg)',
  };
}
